import CampoTexto from "@/components/Formulario/CampoTexto";
import AlertBox from "@/components/layout/AlertBox";
import Grid, { GridItem } from "@/components/layout/Grid";
import { parseTelefone } from "@tera/shared/src/lib";
import { FormCamposProps } from "./FormCamposProps";
import { useQuery } from "react-query";
import axios from "axios";
import CampoSeletor from "@/components/Formulario/CampoSeletor";
import Icon from "@/components/Icon";
import { mdiCircle } from "@mdi/js";
import HBox from "@/components/layout/HBox";
import Skeleton from "@/components/layout/Skeleton";
import VBox from "@/components/layout/VBox";
import CampoCheckbox from "@/components/Formulario/CampoCheckbox";
import { CanalChaveApiCodecWhatsapp, CanalChaveApiWhatsapp } from "@tera/shared/src/canalCodecs/CanalChaveApiCodecWhatsapp";
import CampoMemo from "@/components/Formulario/CampoMemo";

/** Código da Meta para "o cliente recusou compartilhar o histórico". */
const ERRO_HISTORICO_RECUSADO = 2593109;

/**
 * Andamento da importação de contatos e histórico, gravado pelos handlers em
 * `canal.propriedades.coexistencia_sync`. É informativo: a importação roda em
 * fila e pode levar horas.
 */
const StatusImportacaoCoexistencia = ({ sync }: { sync?: Record<string, any> }) => {
    if (!sync) return null;

    if (sync.erro) {
        return (
            <AlertBox variant="warning">
                <span>
                    {sync.erro === ERRO_HISTORICO_RECUSADO
                        ? 'O histórico não foi compartilhado: a opção foi recusada durante a conexão. Os contatos e as conversas novas continuam funcionando normalmente.'
                        : `A importação do histórico falhou (erro ${sync.erro} da Meta).`}
                </span>
            </AlertBox>
        );
    }

    const progresso = sync.progress ?? 0;
    const concluido = sync.concluido || progresso === 100;

    return (
        <AlertBox variant={concluido ? 'success' : 'info'}>
            <span>
                {concluido
                    ? 'Importação de contatos e histórico concluída.'
                    : `Importando contatos e histórico: ${progresso}% concluído. Pode levar algumas horas.`}
            </span>
        </AlertBox>
    );
};

export const FormCamposWhatsapp = ({
    canalId,
    value,
    onChange,
    fieldErrors,
    props,
    onChangeProps,
}: FormCamposProps) => {
    const codec = new CanalChaveApiCodecWhatsapp();
    const dadosApi: CanalChaveApiWhatsapp | null = value ? codec.decode(value) : null;

    const queryTelefones = useQuery({
        queryKey: ['telefones', dadosApi?.phoneNumberId],
        queryFn: async () => {
            if (!dadosApi?.phoneNumberId) return null;
            const response = await axios.get(`/api/canal/${canalId}/whatsapp/telefones`);
            const telefones = response.data?.result || [];
            return telefones
        },
        enabled: !!dadosApi?.phoneNumberId,
    });

    const queryGrupos = useQuery(
        ['usuario_grupo'],
        async () => {
            const res = await fetch('/api/usuario_grupo/box');
            return res.json();
        },
    );

    const gruposOptions =
        queryGrupos.data?.result?.map((it: any) => ({
            id: it.id,
            label: it.nome,
        })) ?? [];

    return (
        <Grid>
            <GridItem sm={12} md={4}>
                {queryTelefones.isLoading ? (
                    <VBox>
                        <Skeleton format="text" />
                        <Skeleton format="card" />
                    </VBox>
                ) : (
                    <CampoSeletor
                        label="Telefone"
                        name="telefone"
                        value={dadosApi?.phoneNumberId || ''}
                        onChange={(value) => {
                            const telefone = queryTelefones.data?.find((t: any) => t.id === value);
                            onChange?.(codec.encode({
                                ...dadosApi,
                                phoneNumberId: telefone?.id || '',
                                phoneNumber: telefone?.numero || '',
                            } as CanalChaveApiWhatsapp));
                        }}
                        options={queryTelefones.data?.map((t: any) => {
                            const qColor: Record<string, string> = {
                                GREEN: 'var(--tc-color-primary)',
                                YELLOW: 'var(--tc-color-warning)',
                                RED: 'var(--tc-color-danger)',
                            }

                            return ({
                                id: t.id,
                                label: <HBox>
                                    <Icon path={mdiCircle} size={0.5} color={qColor[t.qualidade] ?? 'var(--tc-color-gray-400)'} />
                                    <span>{parseTelefone(t.numero)} ({t.nome_verificado})</span>
                                </HBox>,
                            })
                        }) || []}
                        error={fieldErrors?.telefone}
                        isLoading={queryTelefones.isLoading}
                        isDisabled={queryTelefones.isLoading || !queryTelefones.data}
                        placeholder="Selecione um telefone"
                    />
                )}
            </GridItem>
            <GridItem sm={12} md={6}>
                <CampoCheckbox
                    label="Habilitar chamadas de voz"
                    name="habilitar_chamadas"
                    value={dadosApi?.enableCalling || false}
                    onChange={(value) => {
                        onChange?.(codec.encode({
                            ...dadosApi,
                            enableCalling: value,
                        } as CanalChaveApiWhatsapp));
                    }}
                    error={fieldErrors?.nome}
                    placeholder="Nome do canal"
                />
            </GridItem>
            {props?.coexistencia && (
                <>
                    <GridItem sm={12}>
                        <h4 style={{ marginTop: '1rem' }}>Coexistência com o app WhatsApp Business</h4>
                    </GridItem>
                    <GridItem sm={12} md={4}>
                        <CampoSeletor
                            label="Grupo / departamento ao responder pelo celular"
                            name="grupo_responsavel_coexistencia"
                            value={props?.grupo_responsavel_coexistencia}
                            options={gruposOptions}
                            onChange={(v) => {
                                onChangeProps?.({
                                    ...(props ?? {}),
                                    grupo_responsavel_coexistencia: v,
                                });
                            }}
                        />
                    </GridItem>
                    <GridItem sm={12} md={8}>
                        <label>&nbsp;</label>
                        <AlertBox variant="info">
                            <span>
                                Quando alguém responder pelo aplicativo no celular, o atendimento
                                automático é pausado e a conversa entra na fila deste grupo.
                                Sem grupo selecionado, a conversa vai para atendimento sem responsável.
                            </span>
                        </AlertBox>
                    </GridItem>
                    <GridItem sm={12}>
                        <StatusImportacaoCoexistencia sync={props?.coexistencia_sync} />
                    </GridItem>
                </>
            )}

            <GridItem sm={12}>
                <h4 style={{ marginTop: '1rem' }}>Propriedades do atendimento por voz</h4>
            </GridItem>
            <GridItem sm={12} md={4}>
                <CampoMemo
                    label="Mensagem de boas-vindas"
                    name="mensagem_boas_vindas"
                    value={props?.mensagemBoasVindas || ''}
                    onChange={(v) => {
                        onChangeProps?.({
                            ...(props ?? {}),
                            mensagemBoasVindas: v,
                        });
                    }}
                />
            </GridItem>

            <GridItem sm={12} md={4}>
                <CampoSeletor
                    label="Grupo / departamento responsável"
                    name="grupo_responsavel_id"
                    value={props?.grupo_responsavel_id}
                    options={gruposOptions}
                    onChange={(v) => {
                        onChangeProps?.({
                            ...(props ?? {}),
                            grupo_responsavel_id: v,
                        });
                    }}
                />
            </GridItem>

            <GridItem sm={12} md={4}>
                <CampoMemo
                    label="Mensagem de despedida"
                    name="mensagem_despedida"
                    value={props?.mensagemDespedida || ''}
                    onChange={(v) => {
                        onChangeProps?.({
                            ...(props ?? {}),
                            mensagemDespedida: v,
                        });
                    }}
                />
            </GridItem>
            <GridItem sm={12} md={8}>
                <label>&nbsp;</label>
                <AlertBox variant="info">
                    <span>
                        Para editar mais detalhes de canais Whatsapp, visite seu <a href="https://business.facebook.com/wa/manage/home/" target="_blank" rel="noopener noreferrer">Gerenciador do Whatsapp</a>.
                    </span>
                </AlertBox>
            </GridItem>
        </Grid>
    );
};
